import React, { useMemo } from 'react';
import cl from 'classnames';
import { Headset, Package } from 'src/models';
import { RoleEnum } from 'src/security/roles';
import { useHeadsetsState } from 'src/services/headsets';
import { useLanguage } from 'src/hooks';
import { currencySign, daysFromToday, formatDuration } from 'src/helper';
import { Button } from 'src/components/general';
import { ReactComponent as PriceSvg } from 'src/icons/price.svg';
import { ReactComponent as DurationSvg } from 'src/icons/duration.svg';
import styles from './hotelHeadsetsStyle.module.scss';

export const HeadsetsInfoBar: React.FC<{
  headsets: Headset[];
  role?: RoleEnum;
  linkHeadset: () => void;
}> = ({ headsets, role, linkHeadset }) => {
  const { t } = useLanguage();

  const info = useMemo(() => {
    return headsets.reduce(
      (value, record) => {
        value.all += 1;
        if (record.battery <= 15) {
          value.discharged += 1;
        }

        if (!record.alive) {
          const days = daysFromToday(record.lastActivity);
          if (days > 5) {
            value.fiveDays += 1;
          } else if (days > 3) {
            value.threeDays += 1;
          }
        }

        return value;
      },
      {
        all: 0,
        discharged: 0,
        threeDays: 0,
        fiveDays: 0,
      },
    );
  }, [headsets]);

  const hasLinkHeadsetButton = useMemo(
    () => role && [RoleEnum.admin, RoleEnum.engineer].includes(role),
    [role],
  );

  if (role === RoleEnum.airpano) {
    return null;
  }

  return (
    <div className={styles.headsetInfoBar}>
      <div className={styles.valuesSection}>
        <div
          className={cl(
            styles.valueGroup,
            hasLinkHeadsetButton && styles.withLinkHeadsetButton,
          )}
        >
          <div className={styles.value}>
            {t('page.hotel.headsets.totalHeadsets', { count: info.all })}
          </div>
          <div className={styles.value}>
            {t('page.hotel.headsets.discharged', { count: info.discharged })}
          </div>
        </div>
        <div
          className={cl(
            styles.valueGroup,
            hasLinkHeadsetButton && styles.withLinkHeadsetButton,
          )}
        >
          <div className={styles.value}>
            {t('page.hotel.headsets.notOnline3days', { count: info.threeDays })}
          </div>
          <div className={styles.value}>
            {t('page.hotel.headsets.notOnline5days', { count: info.fiveDays })}
          </div>
        </div>
      </div>
      {hasLinkHeadsetButton && (
        <Button
          label={t('page.hotel.headsets.linkHeadset')}
          onClick={linkHeadset}
          design="lightBlue"
          className={styles.linkHeadsetButton}
          autoSize
        />
      )}
    </div>
  );
};

export const PackageCard: React.FC<{
  package: Package;
  currency: string;
  activate?: () => void;
}> = ({
  package: { id, price, name, duration, promo, isKids },
  currency,
  activate,
}) => {
  const { t } = useLanguage();
  const { isPending } = useHeadsetsState();

  return (
    <div
      key={`pkg-${id}`}
      className={cl(styles.packageCard, !activate && styles.withoutButton)}
    >
      <div className={styles.topPkgSection}>
        <div className={styles.cardLabel}>
          <span>
            {t('page.hotel.headsets.cardLabel', {
              price: `${price} ${currencySign(currency)}`,
            })}
          </span>
          {promo && (
            <div className={cl(styles.tag, styles.blue)}>
              {t('general.promo')}
            </div>
          )}
          {isKids && (
            <div className={cl(styles.tag, styles.green)}>
              {t('general.isKids')}
            </div>
          )}
        </div>
        <div className={styles.cardDescription}>{name}</div>
        <div
          className={cl(styles.cardSpecs, !activate && styles.withoutButton)}
        >
          <div className={styles.cardSpec}>
            <PriceSvg />
            <div className={styles.cardSpecLabel}>
              {price} {currencySign(currency)}
            </div>
          </div>
          <div className={styles.cardSpec}>
            <DurationSvg />
            <div className={styles.cardSpecLabel}>
              {formatDuration(duration)}
            </div>
          </div>
        </div>
      </div>
      {activate && (
        <Button
          label={t('page.hotel.headsets.activatePackage')}
          onClick={activate}
          design="lightBlue"
          size="small"
          style={{ width: '100%' }}
          disabled={Boolean(isPending)}
        />
      )}
    </div>
  );
};
